/**
 * Kleda Client App - UI Components
 * Reusable building blocks with Kleda design tokens
 */

import { ReactNode, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { KLEDA_CLAIMS, KLEDA_CONTACT, type FaqItem } from '@platform/kleda-content'
import type { WasserijItem, RouteOptie, VloerkleedGroep, WijzigKosten, BundleDiscount, VloerkleedVorm, PricingBootstrap, AddVloerkleedRequest, EditVloerkleedRequest, AnnuleerResponseData } from '../types'

// =============================================================================
// ICONS
// =============================================================================

const icons = {
  calendar: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  ),
  truck: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  ),
  sparkles: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  ),
  check: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  alert: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  location: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  ),
  rug: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
  x: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  edit: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  ),
  trash: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  ),
  plus: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  ),
  clock: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  refresh: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  ),
  chevronLeft: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  ),
  chevronRight: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  ),
  shield: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  flame: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
    </svg>
  ),
  cloud: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
    </svg>
  ),
  gift: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  ),
}

export type IconName = keyof typeof icons

export function Icon({ name, className = '' }: { name: IconName; className?: string }) {
  return <span className={className}>{icons[name]}</span>
}

// =============================================================================
// CARD
// =============================================================================

interface CardProps {
  children: ReactNode
  className?: string
}

export function Card({ children, className = '' }: CardProps) {
  return (
    <div className={`card ${className}`}>
      {children}
    </div>
  )
}

// =============================================================================
// STATUS HEADER
// =============================================================================

interface StatusHeaderProps {
  /** Niet meer gerenderd in "Stil premium" — behouden voor bestaande call-sites */
  icon?: IconName
  title: string
  subtitle: ReactNode
}

export function StatusHeader({ title, subtitle }: StatusHeaderProps) {
  return (
    <div className="mb-lg">
      <h1 className="font-heading text-[28px] sm:text-[30px] leading-tight tracking-[-0.4px] text-ink mb-xs">
        {title}
      </h1>
      <p className="text-[15.5px] leading-relaxed text-ink-muted">
        {subtitle}
      </p>
    </div>
  )
}

// =============================================================================
// ACTIELIJST - Stille rijen voor secundaire acties (label + actie-link)
// =============================================================================

interface ActionListProps {
  children: ReactNode
  className?: string
}

export function ActionList({ children, className = '' }: ActionListProps) {
  return (
    <div className={`bg-white rounded-premium border border-track shadow-card overflow-hidden divide-y divide-hairline ${className}`}>
      {children}
    </div>
  )
}

interface ActionRowProps {
  label: string
  /** Actie-tekst rechts, bv. "Wijzigen", "Toevoegen", "Bekijken" */
  actionLabel: string
  /** Stille subregel onder het label, bv. de huidige instructie-tekst */
  sublabel?: string
  onClick?: () => void
  href?: string
  danger?: boolean
  disabled?: boolean
}

export function ActionRow({ label, actionLabel, sublabel, onClick, href, danger, disabled }: ActionRowProps) {
  const labelClass = danger ? 'text-red-600' : 'text-ink-soft'
  const actionClass = danger ? 'text-red-500' : 'text-primary'
  const content = (
    <>
      <span className="flex-1 min-w-0">
        <span className={`block text-[14px] ${labelClass}`}>{label}</span>
        {sublabel && (
          <span className="block text-[12px] text-ink-faint italic truncate">{sublabel}</span>
        )}
      </span>
      <span className={`text-[13px] font-bold whitespace-nowrap ${actionClass}`}>
        {actionLabel} →
      </span>
    </>
  )

  if (href) {
    return (
      <a href={href} className="action-row hover:bg-surface-soft transition-colors">
        {content}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="action-row hover:bg-surface-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {content}
    </button>
  )
}

// =============================================================================
// NUMMER-BADGE — klein rondje voor kleed-opsommingen (rapport + overzicht)
// =============================================================================

export function NummerBadge({ nummer, className = '' }: { nummer: number; className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary-dark text-[11px] font-bold flex-shrink-0 ${className}`}>
      {nummer}
    </span>
  )
}

// =============================================================================
// TRUST-REGEL - Canonieke claims als stille regel onder de hoofdkolom
// =============================================================================

export function TrustLine({ className = '' }: { className?: string }) {
  return (
    <p className={`text-[12.5px] text-ink-faint ${className}`}>
      {KLEDA_CLAIMS.garantieKort} · {KLEDA_CLAIMS.reviewKort}
    </p>
  )
}

// =============================================================================
// FAQ LIST — Stille veelgestelde-vragen accordion (vragen per fase)
// =============================================================================

// FaqItem is single-sourced uit @platform/kleda-content (zie KLEDA_FAQ).
// Her-geëxporteerd zodat bestaande imports uit './UI' blijven werken.
export type { FaqItem }

export function FaqList({ items, className = '', title = 'Veelgestelde vragen', onOpen }: { items: FaqItem[]; className?: string; title?: string; onOpen?: (vraag: string) => void }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <div className={className}>
      <p className="section-label mb-sm">{title}</p>
      <div className="bg-white rounded-premium border border-track shadow-card overflow-hidden divide-y divide-hairline">
        {items.map((item, i) => {
          const isOpen = openIndex === i
          return (
            <div key={item.vraag}>
              <button
                type="button"
                onClick={() => {
                  if (!isOpen) onOpen?.(item.vraag)
                  setOpenIndex(isOpen ? null : i)
                }}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-sm px-md py-sm text-left hover:bg-surface-soft transition-colors"
              >
                <span className="text-[14px] text-ink-soft">{item.vraag}</span>
                <svg
                  className={`w-4 h-4 flex-shrink-0 text-ink-faint transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {isOpen && (
                <div className="px-md pb-md text-[13.5px] leading-relaxed text-ink-soft">
                  {item.antwoord}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// STATUS BAR - Premium progress indicator (Airbnb/Stripe style)
// =============================================================================

/**
 * Order process stappen:
 * 1. Besteld → 2. Ophalen → 3. Reinigen → 4. Afleveren → 5. Klaar
 */
type ProcessStep = 'besteld' | 'ophalen' | 'reinigen' | 'afleveren' | 'klaar'

interface StatusBarProps {
  status: string
  className?: string
}

/**
 * Map Salesforce status naar process step
 */
function getProcessStep(status: string): ProcessStep {
  const statusMap: Record<string, ProcessStep> = {
    // Ophalen fase
    'Ophalen plannen': 'ophalen',
    'Ophaalvoorstel gedaan': 'ophalen',
    'Ophaalvoorstel geen reactie': 'ophalen',
    'Ophalen ingepland': 'ophalen',
    'Opgehaald': 'reinigen',

    // Reinigen fase
    'Aan het reinigen': 'reinigen',
    'Kwaliteitscontrole': 'reinigen',

    // Afleveren fase
    'Terugbrengen plannen': 'afleveren',
    'Aflevervoorstel gedaan': 'afleveren',
    'Terugbrengen ingepland': 'afleveren',

    // Klaar
    'Teruggebracht': 'klaar',
    'Afgerond': 'klaar',

    // Zelf-halen-en-brengen-pad (zelfde indeling als Timeline.tsx)
    'Wordt gebracht': 'ophalen',
    'Zelf gebracht': 'reinigen',
    'Wordt opgehaald': 'afleveren',
    'Zelf opgehaald': 'klaar',
  }

  return statusMap[status] || 'besteld'
}

/**
 * Bepaal of een stap is voltooid
 */
function isStepCompleted(currentStep: ProcessStep, checkStep: ProcessStep): boolean {
  const order: ProcessStep[] = ['besteld', 'ophalen', 'reinigen', 'afleveren', 'klaar']
  return order.indexOf(currentStep) > order.indexOf(checkStep)
}

/**
 * Bepaal of een stap actief is
 */
function isStepActive(currentStep: ProcessStep, checkStep: ProcessStep): boolean {
  return currentStep === checkStep
}

const stepConfig: { id: ProcessStep; label: string; icon: JSX.Element }[] = [
  {
    id: 'ophalen',
    label: 'Ophalen',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25m-3.75 0V5.625m0 12.75v-2.22" />
      </svg>
    ),
  },
  {
    id: 'reinigen',
    label: 'Reinigen',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
  },
  {
    id: 'afleveren',
    label: 'Afleveren',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  },
  {
    id: 'klaar',
    label: 'Klaar',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
]

export function StatusBar({ status, className = '' }: StatusBarProps) {
  const currentStep = getProcessStep(status)

  return (
    <div className={`w-full ${className}`}>
      {/* Desktop: Horizontal stepper */}
      <div className="hidden sm:block">
        <div className="relative">
          {/* Progress line background */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-border-subtle" />

          {/* Progress line fill */}
          <div
            className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500 ease-out"
            style={{
              width: currentStep === 'ophalen' ? '0%'
                   : currentStep === 'reinigen' ? '33%'
                   : currentStep === 'afleveren' ? '66%'
                   : currentStep === 'klaar' ? '100%'
                   : '0%'
            }}
          />

          {/* Steps */}
          <div className="relative flex justify-between">
            {stepConfig.map((step) => {
              const completed = isStepCompleted(currentStep, step.id)
              const active = isStepActive(currentStep, step.id)

              return (
                <div key={step.id} className="flex flex-col items-center">
                  {/* Step circle */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      transition-all duration-300 ease-out
                      ${completed
                        ? 'bg-primary text-white'
                        : active
                          ? 'bg-primary text-white ring-4 ring-primary/20'
                          : 'bg-white border-2 border-border-subtle text-ink-muted'
                      }
                    `}
                  >
                    {completed ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      step.icon
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={`
                      mt-2 text-body-sm font-medium transition-colors duration-300
                      ${completed || active ? 'text-ink' : 'text-ink-muted'}
                    `}
                  >
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Mobile: Compact pill indicator */}
      <div className="sm:hidden">
        <div className="flex items-center justify-center gap-1.5">
          {stepConfig.map((step, index) => {
            const completed = isStepCompleted(currentStep, step.id)
            const active = isStepActive(currentStep, step.id)

            return (
              <div key={step.id} className="flex items-center">
                {/* Dot/pill */}
                <div
                  className={`
                    transition-all duration-300
                    ${active
                      ? 'h-2 w-8 rounded-full bg-primary'
                      : completed
                        ? 'h-2 w-2 rounded-full bg-primary'
                        : 'h-2 w-2 rounded-full bg-border-subtle'
                    }
                  `}
                />

                {/* Connector line */}
                {index < stepConfig.length - 1 && (
                  <div
                    className={`
                      w-4 h-0.5 transition-colors duration-300
                      ${completed ? 'bg-primary' : 'bg-border-subtle'}
                    `}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Active step label */}
        <p className="text-center text-body-sm font-medium text-ink mt-2">
          {stepConfig.find(s => s.id === currentStep)?.label || 'Besteld'}
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// ADRES CARD
// =============================================================================

interface AdresCardProps {
  label: string
  adres: string
}

export function AdresCard({ label, adres }: AdresCardProps) {
  // Clean HTML tags from SF formula field
  const cleanAdres = adres?.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '') || ''

  return (
    <div className="flex gap-sm">
      <div className="text-primary mt-0.5">
        <Icon name="location" />
      </div>
      <div>
        <p className="section-label mb-xs">
          {label}
        </p>
        <p className="text-body text-ink whitespace-pre-line">
          {cleanAdres}
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// ITEM LIJST - Gegroepeerd zoals email template
// =============================================================================

interface ItemLijstProps {
  items: WasserijItem[]
  totaalM2?: number
  totaalInclBtw?: number
  totaalOriginelePrijsInclBtw?: number
  totaleKortingInclBtw?: number
}

/**
 * Groepeer items: Primary items worden hoofdkaarten,
 * Package/Extra met gerelateerdeItemId worden bij hun primair item getoond
 */
function groepeerItems(items: WasserijItem[]): VloerkleedGroep[] {
  if (!items || items.length === 0) return []

  // Vind alle primaire items (ALLES met category Primary of zonder gerelateerdeItemId)
  const primaire = items.filter(item =>
    item.productCategory === 'Primary' ||
    (!item.gerelateerdeItemId && item.productCategory !== 'Package')
  )

  // Groepeer per primair item
  return primaire.map(primair => {
    const gekoppeld = items.filter(item => item.gerelateerdeItemId === primair.id)
    const pakket = gekoppeld.find(item => item.productCategory === 'Package')
    const extras = gekoppeld.filter(item =>
      item.productCategory !== 'Primary' && item.productCategory !== 'Package'
    )

    return { primair, pakket, extras }
  })
}

/**
 * Bepaal vorm op basis van afmetingen
 */
function bepaalVorm(item: WasserijItem): string {
  if (item.diameter && item.diameter > 1) {
    return 'Rond'
  }
  if ((item.lengte && item.lengte > 0) || (item.breedte && item.breedte > 0)) {
    return 'Rechthoekig'
  }
  return 'Andere vorm'
}

/**
 * Format prijs met komma als decimaalteken
 */
function formatPrijs(prijs: number | undefined): string {
  if (!prijs) return '–'
  return prijs.toFixed(2).replace('.', ',')
}

/**
 * Format centimeters naar leesbare tekst
 */
function formatCm(cm: number | undefined): string {
  if (!cm) return '–'
  return cm.toString().replace('.', ',')
}

export function ItemLijst({ items, totaalInclBtw, totaalOriginelePrijsInclBtw, totaleKortingInclBtw }: ItemLijstProps) {
  if (!items || items.length === 0) {
    return null
  }

  const groepen = groepeerItems(items)

  // Fallback: als geen groepering mogelijk, toon items gewoon
  if (groepen.length === 0) {
    return (
      <div>
        <p className="section-label mb-sm">
          Items ({items.length})
        </p>
        <div className="space-y-sm">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-sm p-sm bg-surface-soft rounded-kleda-md"
            >
              <div className="text-secondary">
                <Icon name="rug" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-body font-medium text-ink truncate">
                  {item.productNaam || 'Item'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-md">
      {/* Alle items als kaarten */}
      {groepen.map((groep) => (
        <ItemKaart
          key={groep.primair.id}
          groep={groep}
        />
      ))}

      {/* Totaal */}
      {totaalInclBtw && (
        <div className="pt-md border-t border-hairline space-y-xs">
          {/* Korting regel - alleen tonen als er korting is */}
          {totaleKortingInclBtw && totaleKortingInclBtw > 0 && totaalOriginelePrijsInclBtw && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-body-sm text-ink-muted">Subtotaal</span>
                <span className="text-body-sm text-ink-muted">
                  € {formatPrijs(totaalOriginelePrijsInclBtw)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-body-sm text-green-600 font-medium">Bundelkorting</span>
                <span className="text-body-sm text-green-600 font-medium">
                  − € {formatPrijs(totaleKortingInclBtw)}
                </span>
              </div>
            </>
          )}
          <div className="flex justify-between items-center">
            <span className="text-body font-medium text-ink">Totaal</span>
            <span className="text-body-intro font-bold text-primary">
              € {formatPrijs(totaalInclBtw)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// ITEM KAART - Individuele item kaart met productNaam als titel
// =============================================================================

interface ItemKaartProps {
  groep: VloerkleedGroep
}

/**
 * Check of een item afmetingen heeft (voor vloerkleden)
 */
function heeftAfmetingen(item: WasserijItem): boolean {
  return Boolean(
    (item.lengte && item.lengte > 0) ||
    (item.breedte && item.breedte > 0) ||
    (item.diameter && item.diameter > 0) ||
    (item.oppervlakte && item.oppervlakte > 0)
  )
}

function ItemKaart({ groep }: ItemKaartProps) {
  const { primair, pakket, extras } = groep
  const vorm = bepaalVorm(primair)
  const isRond = vorm === 'Rond'
  const isRechthoekig = vorm === 'Rechthoekig'
  const toonAfmetingen = heeftAfmetingen(primair)

  return (
    <div className="bg-surface-soft rounded-kleda-md p-md">
      {/* Header: Product naam + prijs */}
      <div className="flex justify-between items-start mb-md">
        <h3 className="text-body font-semibold text-ink">
          {primair.productNaam || 'Item'}
        </h3>
        {primair.prijsInclBtw && (
          <span className="text-body-intro font-bold text-ink">
            € {formatPrijs(primair.prijsInclBtw)}
          </span>
        )}
      </div>

      {/* Details tabel - alleen tonen als er afmetingen zijn */}
      {toonAfmetingen && (
        <div className="space-y-xs text-body-sm">
          {/* Vorm */}
          <div className="flex justify-between">
            <span className="text-ink-muted">Vorm</span>
            <span className="text-ink">{vorm}</span>
          </div>

          {/* Diameter (rond) */}
          {isRond && primair.diameter && (
            <div className="flex justify-between">
              <span className="text-ink-muted">Diameter</span>
              <span className="text-ink">{formatCm(primair.diameter)} cm</span>
            </div>
          )}

          {/* Afmetingen (rechthoekig) */}
          {isRechthoekig && (primair.lengte || primair.breedte) && (
            <div className="flex justify-between">
              <span className="text-ink-muted">Afmetingen</span>
              <span className="text-ink">
                {formatCm(primair.lengte)} × {formatCm(primair.breedte)} cm
              </span>
            </div>
          )}

          {/* Oppervlakte */}
          {primair.oppervlakte && (
            <div className="flex justify-between">
              <span className="text-ink-muted">Oppervlakte</span>
              <span className="text-ink">{primair.oppervlakte.toString().replace('.', ',')} m²</span>
            </div>
          )}

          {/* Pakket */}
          {pakket && (
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Pakket</span>
              <span className="inline-flex items-center gap-1 bg-secondary/10 text-primary text-body-sm font-medium px-sm py-xs rounded-kleda-sm">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {pakket.productNaam}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Extra's als tags */}
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-xs mt-md">
          {extras.map(extra => (
            <span
              key={extra.id}
              className="inline-block bg-white border border-border-subtle text-ink text-body-sm font-medium px-sm py-xs rounded-kleda-sm"
            >
              {extra.productNaam}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// ROUTE KIEZER
// =============================================================================

interface RouteKiezerProps {
  opties: RouteOptie[]
  selected: number | null
  onSelect: (id: number) => void
  disabled?: boolean
}

export function RouteKiezer({ opties, selected, onSelect, disabled }: RouteKiezerProps) {
  if (!opties || opties.length === 0) {
    return (
      <p className="text-body text-ink-muted text-center py-lg">
        Er zijn momenteel geen beschikbare opties.
      </p>
    )
  }

  return (
    <div className="space-y-sm">
      {opties.map((optie) => (
        <button
          key={optie.id}
          type="button"
          disabled={disabled}
          onClick={() => optie.id && onSelect(optie.id)}
          className={`route-option w-full text-left ${
            selected === optie.id ? 'selected' : ''
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {/* Radio indicator */}
          <div className={`
            w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
            ${selected === optie.id
              ? 'border-primary bg-primary'
              : 'border-border-subtle'
            }
          `}>
            {selected === optie.id && (
              <div className="w-2 h-2 rounded-full bg-white" />
            )}
          </div>

          {/* Label */}
          <span className="text-body text-ink">
            {optie.label}
          </span>
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// OPMERKINGEN VELD
// =============================================================================

interface OpmerkingenVeldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

export function OpmerkingenVeld({
  value,
  onChange,
  placeholder = 'Bijvoorbeeld: graag bellen bij aankomst',
  disabled
}: OpmerkingenVeldProps) {
  return (
    <div>
      <label className="block text-body font-medium text-ink mb-xs">
        Opmerkingen (optioneel)
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        className="textarea"
      />
    </div>
  )
}

// =============================================================================
// LOADING
// =============================================================================

export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-xl">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export function LoadingScreen({ message = 'Laden...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-canvas">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-md" />
        <p className="text-body text-ink-muted">{message}</p>
      </div>
    </div>
  )
}

// =============================================================================
// SKELETON LOADERS
// =============================================================================

export function SkeletonPulse({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-border-subtle rounded-kleda-md ${className}`} />
  )
}

export function SkeletonCard() {
  return (
    <div className="card animate-pulse">
      <div className="space-y-md">
        <div className="flex items-center gap-sm">
          <div className="w-5 h-5 rounded-full bg-border-subtle" />
          <div className="flex-1">
            <div className="h-3 bg-border-subtle rounded w-20 mb-xs" />
            <div className="h-4 bg-border-subtle rounded w-48" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function SkeletonRouteOptions() {
  return (
    <div className="card animate-pulse">
      <div className="h-3 bg-border-subtle rounded w-24 mb-md" />
      <div className="space-y-sm">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-md p-md border-2 border-border-subtle rounded-kleda-lg">
            <div className="w-5 h-5 rounded-full bg-border-subtle" />
            <div className="flex-1">
              <div className="h-4 bg-border-subtle rounded w-52" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SkeletonKiesAfspraakView() {
  return (
    <div className="max-w-container mx-auto px-md py-lg sm:py-xl">
      {/* Status bar skeleton */}
      <div className="flex justify-center gap-1.5 mb-lg">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-2 w-8 rounded-full bg-border-subtle animate-pulse" />
        ))}
      </div>

      {/* Header skeleton */}
      <div className="text-center mb-lg animate-pulse">
        <div className="w-16 h-16 rounded-full bg-border-subtle mx-auto mb-md" />
        <div className="h-8 bg-border-subtle rounded w-56 mx-auto mb-xs" />
        <div className="h-5 bg-border-subtle rounded w-72 mx-auto" />
      </div>

      {/* Address card skeleton */}
      <SkeletonCard />

      <div className="h-md" />

      {/* Route options skeleton */}
      <SkeletonRouteOptions />

      <div className="h-md" />

      {/* Submit button skeleton */}
      <div className="h-12 bg-border-subtle rounded-kleda-sm animate-pulse" />
    </div>
  )
}

// =============================================================================
// SUCCESS CONFIRMATION WITH CONFETTI
// =============================================================================

interface ConfettiPiece {
  id: number
  x: number
  delay: number
  duration: number
  color: string
}

function Confetti() {
  const pieces: ConfettiPiece[] = useMemo(() => {
    const colors = ['#2b7c9c', '#74bfb7', '#5faec9', '#9ed9d2', '#FFD700', '#FF6B6B']
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)]
    }))
  }, [])

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute w-3 h-3 rounded-sm animate-confetti"
          style={{
            left: `${piece.x}%`,
            backgroundColor: piece.color,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        />
      ))}
    </div>
  )
}

interface SuccessConfirmationProps {
  title: string
  subtitle: string
  details?: {
    label: string
    value: string
  }[]
  ctaText?: string
  onCta?: () => void
  showConfetti?: boolean
}

export function SuccessConfirmation({
  title,
  subtitle,
  details,
  ctaText,
  onCta,
  showConfetti = true
}: SuccessConfirmationProps) {
  const [showConfettiEffect, setShowConfettiEffect] = useState(showConfetti)

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfettiEffect(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [showConfetti])

  return (
    <>
      {showConfettiEffect && <Confetti />}
      <div className="text-center animate-fade-in">
        {/* Success checkmark with animation */}
        <div className="relative inline-flex items-center justify-center w-20 h-20 mb-lg">
          <div className="absolute inset-0 bg-secondary/20 rounded-full animate-ping" style={{ animationDuration: '1.5s' }} />
          <div className="relative w-20 h-20 bg-secondary rounded-full flex items-center justify-center animate-scale-in">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" className="animate-draw-check" />
            </svg>
          </div>
        </div>

        <h1 className="font-heading text-heading-main text-ink mb-xs animate-slide-up" style={{ animationDelay: '0.2s' }}>
          {title}
        </h1>
        <p className="text-body-intro text-ink-muted mb-lg animate-slide-up" style={{ animationDelay: '0.3s' }}>
          {subtitle}
        </p>

        {/* Details */}
        {details && details.length > 0 && (
          <div className="bg-surface-soft rounded-kleda-lg p-lg mb-lg text-left animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <div className="space-y-md">
              {details.map((detail, index) => (
                <div key={index} className="flex items-start gap-sm">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="check" className="w-3 h-3 text-primary" />
                  </div>
                  <div>
                    <p className="text-body-sm text-ink-muted">{detail.label}</p>
                    <p className="text-body font-medium text-ink">{detail.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Button */}
        {ctaText && onCta && (
          <button
            onClick={onCta}
            className="btn-secondary animate-slide-up"
            style={{ animationDelay: '0.5s' }}
          >
            {ctaText}
          </button>
        )}

        {/* What happens next */}
        <div className="mt-xl pt-lg border-t border-hairline animate-slide-up" style={{ animationDelay: '0.6s' }}>
          <p className="section-label mb-md">
            Wat nu?
          </p>
          <div className="space-y-sm text-left">
            <div className="flex items-start gap-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-body-sm font-medium text-primary">1</span>
              </div>
              <p className="text-body text-ink-muted">
                Je ontvangt een bevestiging per e-mail
              </p>
            </div>
            <div className="flex items-start gap-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-body-sm font-medium text-primary">2</span>
              </div>
              <p className="text-body text-ink-muted">
                De dag ervoor ontvang je per e-mail het exacte tijdsblok van 2 uur
              </p>
            </div>
            <div className="flex items-start gap-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-body-sm font-medium text-primary">3</span>
              </div>
              <p className="text-body text-ink-muted">
                Niet thuis? Geef via de instructies in de app een veilige plek door
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// =============================================================================
// ONBOARDING STEPS - What to expect
// =============================================================================

interface OnboardingStep {
  icon: IconName
  title: string
  description: string
}

interface OnboardingStepsProps {
  steps: OnboardingStep[]
  className?: string
}

export function OnboardingSteps({ steps, className = '' }: OnboardingStepsProps) {
  return (
    <div className={`bg-surface-soft rounded-kleda-lg p-lg ${className}`}>
      <p className="section-label mb-md">
        Hoe werkt het?
      </p>
      <div className="space-y-md">
        {steps.map((step, index) => (
          <div key={index} className="flex items-start gap-sm">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={step.icon} className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-body font-medium text-ink">{step.title}</p>
              <p className="text-body-sm text-ink-muted">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// AFSPRAAK INFO - Hero Card Style
// =============================================================================

interface AfspraakInfoProps {
  label: string
  moment?: string
  isDefinitief?: boolean
}

export function AfspraakInfo({ label, moment, isDefinitief }: AfspraakInfoProps) {
  if (!moment) return null

  // Parse moment voor betere weergave
  // Format verwacht: "donderdag 12 maart van 09:00 tot 14:00"
  const dagMatch = moment.match(/^(\w+)\s+(\d{1,2})\s+(\w+)/i)
  const tijdMatch = moment.match(/(\d{1,2}:\d{2}).*?(\d{1,2}:\d{2})/)

  const dagNaam = dagMatch ? dagMatch[1] : ''
  const dagNummer = dagMatch ? dagMatch[2] : ''
  const maand = dagMatch ? dagMatch[3] : ''
  const startTijd = tijdMatch ? tijdMatch[1] : ''
  const eindTijd = tijdMatch ? tijdMatch[2] : ''

  return (
    <div>
      <p className="section-label mb-sm">
        {label}
      </p>

      <div className="flex items-center gap-md">
        {/* Datum badge */}
        {dagNummer && (
          <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-kleda-md px-md py-sm text-center min-w-[4.5rem] shadow-sm">
            <p className="text-2xl font-bold leading-tight">{dagNummer}</p>
            <p className="text-body-sm font-medium opacity-90 lowercase">{maand}</p>
          </div>
        )}

        {/* Details — alleen de dagnaam met hoofdletter, maand klein (NL) */}
        <div className="flex-1">
          <p className="text-body font-semibold text-ink">
            {dagNaam ? dagNaam.charAt(0).toUpperCase() + dagNaam.slice(1).toLowerCase() : ''} {dagNummer} {maand.toLowerCase()}
          </p>
          {startTijd && eindTijd && (
            <div className="flex items-center gap-xs mt-xs text-ink-muted">
              <Icon name="clock" className="w-4 h-4" />
              <span className="text-body">{startTijd} - {eindTijd}</span>
            </div>
          )}
          {/* "Definitief tijdsblok" alléén bij het krappe 2-uurs blok (dag
              ervoor bekend) — daarvoor is de afspraak wel bevestigd maar het
              getoonde blok nog ruim. */}
          <span className="inline-flex items-center gap-xs mt-sm text-body-sm text-secondary bg-secondary/10 px-sm py-xs rounded-full">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {isDefinitief ? 'Definitief tijdsblok' : 'Afspraak bevestigd'}
          </span>
        </div>
      </div>

      {/* Fallback als parsing faalt */}
      {!dagNummer && (
        <p className="text-body font-medium text-ink mt-sm">
          {moment}
        </p>
      )}
    </div>
  )
}

// =============================================================================
// MODAL BASE
// =============================================================================

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

export function Modal({ isOpen, onClose, title, children }: ModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <div className="relative bg-white rounded-kleda-lg shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-md border-b border-hairline">
            <h2 className="font-heading text-heading-card text-ink">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink transition-colors"
            >
              <Icon name="x" />
            </button>
          </div>

          {/* Content */}
          <div className="p-md">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// KOSTEN BANNER
// =============================================================================

interface KostenBannerProps {
  kosten: WijzigKosten
  type: 'wijzigen' | 'annuleren'
}

export function KostenBanner({ kosten, type }: KostenBannerProps) {
  const isGratis = kosten.bedrag === 0

  if (isGratis) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-kleda-md p-md mb-md">
        <div className="flex items-center gap-sm">
          <div className="text-green-600">
            <Icon name="check" />
          </div>
          <div>
            <p className="text-body font-medium text-green-800">
              Gratis {type === 'wijzigen' ? 'wijzigen' : 'annuleren'}
            </p>
            <p className="text-body-sm text-green-600">
              Meer dan 48 uur van tevoren
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-kleda-md p-md mb-md">
      <div className="flex items-center gap-sm">
        <div className="text-amber-600">
          <Icon name="alert" />
        </div>
        <div>
          <p className="text-body font-medium text-amber-800">
            {type === 'wijzigen' ? 'Wijzigingskosten' : 'Annuleringskosten'}: € {kosten.bedrag.toFixed(2).replace('.', ',')}
          </p>
          <p className="text-body-sm text-amber-600">
            {kosten.message}
          </p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// WIJZIG AFSPRAAK MODAL
// =============================================================================

interface WijzigAfspraakModalProps {
  isOpen: boolean
  onClose: () => void
  routeOpties: RouteOptie[]
  extraRouteOpties?: RouteOptie[]
  huidigeAfspraak?: string
  /**
   * Datum van de huidige afspraak (yyyy-mm-dd). Markeert dat moment in de
   * lijst als "je huidige moment" in plaats van het als gewone keuze aan te
   * bieden — verzetten naar de dag die je al hebt is geen keuze maar
   * verwarring (09-09).
   */
  huidigeDatum?: string
  /** Ophaal of aflever; bepaalt alleen de woordkeus in de kop en de uitleg. */
  fase?: 'pickup' | 'delivery'
  kosten?: WijzigKosten
  /**
   * Startdatum van de opslagklok (moment aflevervoorstel). Aanwezig = de
   * opslag-badges tonen bij elk moment, net als in de kies-flow. Zonder deze
   * prop koos de klant hier blind een datum die geld kost (20-08).
   */
  opslagkostenStartDatum?: string
  onWijzig: (routeId: string, opmerkingen?: string) => Promise<void>
  onLaadMeerOpties?: () => Promise<void>
}

export function WijzigAfspraakModal({
  isOpen,
  onClose,
  routeOpties,
  extraRouteOpties,
  huidigeAfspraak,
  huidigeDatum,
  fase = 'pickup',
  kosten,
  opslagkostenStartDatum,
  onWijzig,
  onLaadMeerOpties
}: WijzigAfspraakModalProps) {
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null)
  const [opmerkingen, setOpmerkingen] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLading, setIsLading] = useState(false)
  const [visibleExtraCount, setVisibleExtraCount] = useState(0)
  const [extraLoaded, setExtraLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const EXTRA_BATCH_SIZE = 5
  const extraOpties = extraRouteOpties ?? []
  const alleOpties = [...routeOpties, ...extraOpties.slice(0, visibleExtraCount)]
  const hasMoreExtra = visibleExtraCount < extraOpties.length
  // Tijdens render afgeleid, niet in een effect: het label van het gekozen
  // moment wordt in de bezig-melding en de bevestiging genoemd.
  const geselecteerdLabel = selectedRouteId
    ? alleOpties.find((o) => o.routeId === selectedRouteId)?.label ?? null
    : null

  async function handleLoadMore() {
    if (!extraLoaded && onLaadMeerOpties) {
      // Eerste keer: API call om extra opties te laden
      setIsLading(true)
      try {
        await onLaadMeerOpties()
        setExtraLoaded(true)
        setVisibleExtraCount(EXTRA_BATCH_SIZE)
      } finally {
        setIsLading(false)
      }
    } else {
      // Volgende keren: toon meer uit reeds geladen data (instant)
      setVisibleExtraCount(prev => prev + EXTRA_BATCH_SIZE)
    }
  }

  async function handleSubmit() {
    if (!selectedRouteId) {
      setError('Kies eerst een nieuw moment')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await onWijzig(selectedRouteId, opmerkingen || undefined)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleSelect(optie: RouteOptie) {
    // Altijd routeId gebruiken voor betrouwbare selectie
    if (optie.routeId) {
      setSelectedRouteId(optie.routeId)
    }
    setError(null)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Afspraak wijzigen">
      {/* Huidige afspraak — altijd een blok, ook als het moment onbekend is.
          Verzetten begint met weten wat je nú hebt; het stil weglaten liet de
          klant kiezen zonder vertrekpunt (09-09). */}
      <div className="bg-surface-soft rounded-kleda-md p-md mb-md">
        <p className="section-label mb-xs">
          {fase === 'delivery' ? 'Huidige aflevering' : 'Huidige ophaalafspraak'}
        </p>
        {huidigeAfspraak ? (
          <p className="text-body font-medium text-ink">{huidigeAfspraak}</p>
        ) : (
          <p className="text-body-sm text-ink-muted">
            We kunnen je huidige moment hier even niet laten zien. Kies hieronder
            het moment dat je wilt, of bel {KLEDA_CONTACT.telefoonWeergave} als je
            eerst wilt weten wanneer we nu komen.
          </p>
        )}
      </div>

      {/* Kosten banner */}
      {kosten && <KostenBanner kosten={kosten} type="wijzigen" />}

      {/* Route opties */}
      <div className="mb-md">
        <p className="section-label mb-sm">
          Kies een nieuw moment
        </p>
        {/* Ruimer dan max-h-64: met drie zichtbare regels en een interne
            scrollbalk leek de lijst compleet terwijl de meeste datums
            eronder zaten (09-09). */}
        <div className="space-y-sm max-h-[22rem] overflow-y-auto">
          {alleOpties.map((optie) => {
            // Altijd vergelijken op routeId voor consistente selectie
            const isSelected = selectedRouteId === optie.routeId
            // De dag die de klant al heeft is geen keuze: als hij als gewone
            // optie tussen de rest staat, "verzet" iemand naar zijn eigen
            // datum en verandert er niets.
            const isHuidige = huidigeDatum != null && optie.datum === huidigeDatum
            return (
              <button
                key={optie.id ?? optie.routeId}
                type="button"
                onClick={() => (isHuidige ? undefined : handleSelect(optie))}
                disabled={isSubmitting || isHuidige}
                aria-current={isHuidige ? 'date' : undefined}
                className={`route-option w-full text-left ${isSelected ? 'selected' : ''} ${
                  isHuidige ? 'opacity-70 cursor-default' : ''
                }`}
              >
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'border-primary bg-primary' : 'border-border-subtle'}
                `}>
                  {isSelected ? <div className="w-2 h-2 rounded-full bg-white" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-body text-ink truncate">{optie.label}</span>
                    {isHuidige ? (
                      /* Kort houden: de volledige afspraak staat al bovenaan,
                         en een lange chip knipt hier het tijdslot weg. */
                      <span className="flex-shrink-0 rounded-full bg-surface-soft px-sm py-[2px] text-[12px] font-medium text-ink-muted">
                        Nu gepland
                      </span>
                    ) : (
                      /* Zelfde badge als in de kies-flow: verzetten naar een later
                         moment kost opslag, en dat hoort zichtbaar te zijn vóór
                         de klik, niet pas op de factuur. */
                      <OpslagkostenBadge
                        startDatum={opslagkostenStartDatum}
                        afleverDatum={optie.datum}
                        compact
                        className="flex-shrink-0"
                      />
                    )}
                  </div>
                  {optie.capaciteit && optie.capaciteit <= 3 && !isHuidige ? (
                    <span className="text-body-sm text-amber-600">
                      Nog {optie.capaciteit} {optie.capaciteit === 1 ? 'plek' : 'plekken'}
                    </span>
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>

        {/* Uitleg, zodat "+€40" niet als bedrag bóvenop het huidige bedrag
            leest: het vervangt de opslag die er nu staat. */}
        {opslagkostenStartDatum && (
          <p className="mt-sm text-[13px] text-ink-faint">
            {KLEDA_CLAIMS.opslag} Kies je een later moment, dan vervangt het
            nieuwe bedrag de opslagkosten die nu op je bestelling staan.
          </p>
        )}

        {/* Meer opties laden */}
        {onLaadMeerOpties && (!extraLoaded || hasMoreExtra) && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLading}
            className="w-full mt-sm text-center text-primary hover:text-primary-dark text-body-sm font-medium py-sm"
          >
            {isLading ? 'Laden...' : 'Toon meer datums'}
          </button>
        )}
      </div>

      {/* Opmerkingen */}
      <div className="mb-md">
        <label className="block text-body font-medium text-ink mb-xs">
          Reden wijziging (optioneel)
        </label>
        <textarea
          value={opmerkingen}
          onChange={(e) => setOpmerkingen(e.target.value)}
          placeholder="Bijvoorbeeld: vakantie, andere afspraak"
          rows={2}
          className="textarea"
          disabled={isSubmitting}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body">
          {error}
        </div>
      )}

      {/* Bezig-melding. Het verzetten duurt echt lang (gemeten 09-09: 17 s
          voor de boeking zelf), en een knop die alleen "Wijzigen..." zegt
          leest als een vastgelopen pagina. Zeg wat er gebeurt en dat het even
          duurt, dan wacht de klant in plaats van weg te klikken of te bellen. */}
      {isSubmitting ? (
        <div className="mb-md flex items-start gap-sm rounded-kleda-md border border-hairline bg-surface-soft px-md py-sm">
          <svg className="mt-[2px] h-4 w-4 flex-shrink-0 animate-spin text-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-body-sm text-ink-soft" role="status">
            We zetten je afspraak om naar {geselecteerdLabel ?? 'het gekozen moment'}.
            Dit duurt een halve minuut, omdat we de route opnieuw indelen. Sluit dit
            venster niet.
          </p>
        </div>
      ) : null}

      {/* Buttons */}
      <div className="flex gap-sm">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn-outline flex-1"
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedRouteId || isSubmitting}
          className="btn-primary flex-1"
        >
          {isSubmitting ? 'Bezig met omzetten...' : kosten && kosten.bedrag > 0
            ? `Wijzigen (€ ${kosten.bedrag.toFixed(2).replace('.', ',')})`
            : 'Wijzigen'}
        </button>
      </div>
    </Modal>
  )
}

// =============================================================================
// ANNULEREN MODAL
// =============================================================================

interface AnnulerenModalProps {
  isOpen: boolean
  onClose: () => void
  kosten?: WijzigKosten
  onAnnuleer: (reden?: string) => Promise<AnnuleerResponseData | null>
  onComplete?: () => void
}

export function AnnulerenModal({ isOpen, onClose, kosten, onAnnuleer, onComplete }: AnnulerenModalProps) {
  const [reden, setReden] = useState('')
  const [bevestigd, setBevestigd] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successData, setSuccessData] = useState<AnnuleerResponseData | null>(null)

  async function handleSubmit() {
    if (!bevestigd) {
      setError('Bevestig dat je de order wilt annuleren')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await onAnnuleer(reden || undefined)
      if (result?.feePaymentUrl) {
        // Toon success-state met "Nu betalen"-CTA
        setSuccessData(result)
      } else {
        onClose()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handlePayNow() {
    if (successData?.feePaymentUrl) {
      window.location.href = successData.feePaymentUrl
    }
  }

  function handlePayLater() {
    onComplete?.()
    onClose()
  }

  // Success-state: order is geannuleerd, fee-WO + payment link aangemaakt
  if (successData?.feePaymentUrl) {
    const bedrag = successData.feeAmount ?? 0
    return (
      <Modal isOpen={isOpen} onClose={handlePayLater} title="Order geannuleerd">
        <div className="bg-green-50 border border-green-200 rounded-kleda-md p-md mb-md">
          <div className="flex items-start gap-sm">
            <div className="text-green-600 mt-0.5">
              <Icon name="check" />
            </div>
            <div>
              <p className="text-body font-medium text-green-800">
                Je order is geannuleerd
              </p>
              <p className="text-body-sm text-green-700 mt-xs">
                Betaal direct de annuleringskosten van € {bedrag.toFixed(2).replace('.', ',')}.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-sm">
          <button
            type="button"
            onClick={handlePayLater}
            className="btn-outline flex-1"
          >
            Later betalen
          </button>
          <button
            type="button"
            onClick={handlePayNow}
            className="btn-primary flex-1"
          >
            Nu betalen
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bestelling annuleren">
      {/* Waarschuwing */}
      <div className="bg-red-50 border border-red-200 rounded-kleda-md p-md mb-md">
        <div className="flex items-start gap-sm">
          <div className="text-red-600 mt-0.5">
            <Icon name="alert" />
          </div>
          <div>
            <p className="text-body font-medium text-red-800">
              Weet je zeker dat je wilt annuleren?
            </p>
            <p className="text-body-sm text-red-600 mt-xs">
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
          </div>
        </div>
      </div>

      {/* Kosten banner */}
      {kosten && <KostenBanner kosten={kosten} type="annuleren" />}

      {/* Kosten tabel */}
      <div className="bg-surface-soft rounded-kleda-md p-md mb-md">
        <p className="text-body-sm font-medium text-ink mb-sm">
          Annuleringsvoorwaarden:
        </p>
        <div className="space-y-xs text-body-sm">
          <div className="flex justify-between">
            <span className="text-ink-muted">Tot 48 uur van tevoren</span>
            <span className="text-green-600 font-medium">gratis</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-muted">48-24 uur van tevoren</span>
            <span className="text-ink">€ 20,00</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-muted">24-0 uur van tevoren</span>
            <span className="text-ink">€ 25,00</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-muted">Vergeefse rit door jou, binnen het afgesproken tijdvak</span>
            <span className="text-ink">€ 25,00</span>
          </div>
        </div>
      </div>

      {/* Reden */}
      <div className="mb-md">
        <label className="block text-body font-medium text-ink mb-xs">
          Reden annulering (optioneel)
        </label>
        <textarea
          value={reden}
          onChange={(e) => setReden(e.target.value)}
          placeholder="Bijvoorbeeld: geen behoefte meer, verkeerd besteld"
          rows={2}
          className="textarea"
          disabled={isSubmitting}
        />
      </div>

      {/* Bevestiging checkbox */}
      <label className="flex items-start gap-sm mb-md cursor-pointer">
        <input
          type="checkbox"
          checked={bevestigd}
          onChange={(e) => {
            setBevestigd(e.target.checked)
            setError(null)
          }}
          disabled={isSubmitting}
          className="mt-1 w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary"
        />
        <span className="text-body text-ink">
          Ik begrijp dat deze annulering definitief is
          {kosten && kosten.bedrag > 0 && (
            <> en dat er € {kosten.bedrag.toFixed(2).replace('.', ',')} in rekening wordt gebracht</>
          )}
        </span>
      </label>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body">
          {error}
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-sm">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn-outline flex-1"
        >
          Terug
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!bevestigd || isSubmitting}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-sm px-md rounded-kleda-md transition-colors flex-1 disabled:opacity-50"
        >
          {isSubmitting ? 'Annuleren...' : 'Definitief annuleren'}
        </button>
      </div>
    </Modal>
  )
}

// =============================================================================
// ACTIE KNOPPEN - Stripe-style
// =============================================================================

interface ActieKnoppenProps {
  kanWijzigen: boolean
  kanAnnuleren: boolean
  onWijzigen: () => void
  onAnnuleren: () => void
}

export function ActieKnoppen({ kanWijzigen, kanAnnuleren, onWijzigen, onAnnuleren }: ActieKnoppenProps) {
  if (!kanWijzigen && !kanAnnuleren) return null

  return (
    <div className="flex gap-sm mt-lg pt-md border-t border-hairline">
      {kanWijzigen && (
        <button
          type="button"
          onClick={onWijzigen}
          className="flex-1 flex items-center justify-center gap-sm py-md px-lg bg-white border border-primary text-primary font-medium rounded-full hover:bg-primary hover:text-white transition-all duration-200"
        >
          <Icon name="edit" className="w-5 h-5" />
          <span>Wijzigen</span>
        </button>
      )}
      {kanAnnuleren && (
        <button
          type="button"
          onClick={onAnnuleren}
          className="flex-1 flex items-center justify-center gap-sm py-md px-lg bg-white border border-gray-200 text-gray-600 font-medium rounded-full hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
        >
          <Icon name="trash" className="w-5 h-5" />
          <span>Annuleren</span>
        </button>
      )}
    </div>
  )
}

// =============================================================================
// VLOERKLEED TOEVOEGEN MODAL
// =============================================================================

const BTW_PERCENTAGE = 1.21

/** Voorinvulling voor de edit-modus van de vloerkleed-modal */
export interface VloerkleedEditTarget {
  itemId: string
  vorm: VloerkleedVorm
  lengteCm?: number
  breedteCm?: number
  diameterCm?: number
  oppervlakteM2?: number
  packageSku?: string
  /** Nummer in het bestelling-overzicht, voor de modal-titel ("Vloerkleed 2 wijzigen") */
  volgnummer?: number
}

interface VloerkleedToevoegenModalProps {
  isOpen: boolean
  onClose: () => void
  pricing: PricingBootstrap | null
  isLoadingPricing: boolean
  onAddVloerkleed?: (request: Omit<AddVloerkleedRequest, 'token'>) => Promise<boolean>
  token: string
  /** Edit-modus: vooringevuld bestaand kleed; submit gaat via onEditVloerkleed */
  editTarget?: VloerkleedEditTarget | null
  onEditVloerkleed?: (request: Omit<EditVloerkleedRequest, 'token'>) => Promise<boolean>
}

/**
 * Bereken oppervlakte in m² op basis van vorm en afmetingen
 */
function berekenOppervlakte(
  vorm: VloerkleedVorm,
  lengteCm?: number,
  breedteCm?: number,
  diameterCm?: number,
  oppervlakteM2?: number
): number {
  if (vorm === 'rechthoekig' && lengteCm && breedteCm) {
    return (lengteCm / 100) * (breedteCm / 100)
  }
  if (vorm === 'rond' && diameterCm) {
    const straal = (diameterCm / 100) / 2
    return Math.PI * straal * straal
  }
  if (vorm === 'overig' && oppervlakteM2) {
    return oppervlakteM2
  }
  return 0
}

/**
 * Bereken prijs exclusief BTW voor een nieuw vloerkleed
 */
function berekenPrijsExBtw(
  oppervlakteM2: number,
  basePricePerM2: number,
  packagePercentage: number,
  regioFactor: number
): number {
  if (oppervlakteM2 <= 0) return 0
  const packageMultiplier = 1 + (packagePercentage / 100)
  return oppervlakteM2 * basePricePerM2 * packageMultiplier * regioFactor
}

/**
 * Bepaal welke bundelkorting van toepassing is
 */
function bepaalBundelkorting(
  nieuwAantalKleden: number,
  bundleDiscounts: BundleDiscount[]
): BundleDiscount | null {
  // Sort descending by minCount, pick first that qualifies
  const sorted = [...bundleDiscounts].sort((a, b) => b.minCount - a.minCount)
  return sorted.find(d => nieuwAantalKleden >= d.minCount) || null
}

export function VloerkleedToevoegenModal({
  isOpen,
  onClose,
  pricing,
  isLoadingPricing,
  onAddVloerkleed,
  editTarget,
  onEditVloerkleed
}: VloerkleedToevoegenModalProps) {
  const isEdit = !!editTarget

  // Vorm selectie
  const [vorm, setVorm] = useState<VloerkleedVorm>('rechthoekig')

  // Afmetingen
  const [lengteCm, setLengteCm] = useState<string>('')
  const [breedteCm, setBreedteCm] = useState<string>('')
  const [diameterCm, setDiameterCm] = useState<string>('')
  const [oppervlakteM2, setOppervlakteM2] = useState<string>('')

  // Package selectie
  const [selectedPackageSku, setSelectedPackageSku] = useState<string>('')

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Reset state when modal opens (prefill in edit-modus)
  useEffect(() => {
    if (isOpen) {
      setVorm(editTarget?.vorm ?? 'rechthoekig')
      setLengteCm(editTarget?.lengteCm != null ? String(editTarget.lengteCm) : '')
      setBreedteCm(editTarget?.breedteCm != null ? String(editTarget.breedteCm) : '')
      setDiameterCm(editTarget?.diameterCm != null ? String(editTarget.diameterCm) : '')
      setOppervlakteM2(editTarget?.oppervlakteM2 != null ? String(editTarget.oppervlakteM2) : '')
      setSelectedPackageSku(editTarget?.packageSku ?? '')
      setError(null)
      setSuccess(false)
    }
  }, [isOpen, editTarget])

  // Auto-select "Meest gekozen" package
  useEffect(() => {
    if (pricing?.packages && !selectedPackageSku) {
      const meestGekozen = pricing.packages.find(p => p.isMeestGekozen)
      if (meestGekozen) {
        setSelectedPackageSku(meestGekozen.sku)
      } else if (pricing.packages.length > 0) {
        setSelectedPackageSku(pricing.packages[0].sku)
      }
    }
  }, [pricing?.packages, selectedPackageSku])

  // Bereken live prijs
  const liveCalc = useMemo(() => {
    if (!pricing) return null

    const oppervlakte = berekenOppervlakte(
      vorm,
      parseFloat(lengteCm) || undefined,
      parseFloat(breedteCm) || undefined,
      parseFloat(diameterCm) || undefined,
      parseFloat(oppervlakteM2) || undefined
    )

    if (oppervlakte <= 0) return null

    const selectedPackage = pricing.packages.find(p => p.sku === selectedPackageSku)
    if (!selectedPackage) return null

    const prijsExBtw = berekenPrijsExBtw(
      oppervlakte,
      pricing.basePricePerM2,
      selectedPackage.percentage,
      pricing.regioFactor
    )
    const prijsInclBtw = prijsExBtw * BTW_PERCENTAGE

    // Bereken of bundelkorting van toepassing wordt.
    // In edit-modus verbergen we de bundelkorting-preview: het aantal kleden
    // verandert niet en de m²-benadering hieronder zou het kleed dubbel tellen.
    const nieuwAantalKleden = pricing.bestaandeAantalKleden + (isEdit ? 0 : 1)
    const bundelkorting = isEdit ? null : bepaalBundelkorting(nieuwAantalKleden, pricing.bundleDiscounts)

    // Bestaande subtotaal + nieuwe item
    // Note: dit is een benadering - de server berekent het exacte bedrag
    const bestaandSubtotaal = pricing.bestaandeTotaalM2 * pricing.basePricePerM2 * pricing.regioFactor
    const nieuwSubtotaal = bestaandSubtotaal + prijsExBtw

    let kortingBedrag = 0
    if (bundelkorting) {
      kortingBedrag = nieuwSubtotaal * bundelkorting.discountPercent
    }

    return {
      oppervlakte: Math.round(oppervlakte * 100) / 100,
      prijsExBtw: Math.round(prijsExBtw * 100) / 100,
      prijsInclBtw: Math.round(prijsInclBtw * 100) / 100,
      bundelkorting,
      kortingBedrag: Math.round(kortingBedrag * 100) / 100,
      nieuwTotaalInclBtw: Math.round((nieuwSubtotaal - kortingBedrag) * BTW_PERCENTAGE * 100) / 100
    }
  }, [vorm, lengteCm, breedteCm, diameterCm, oppervlakteM2, selectedPackageSku, pricing, isEdit])

  // Validatie
  const isValid = useMemo(() => {
    if (!selectedPackageSku) return false
    if (vorm === 'rechthoekig') {
      return parseFloat(lengteCm) > 0 && parseFloat(breedteCm) > 0
    }
    if (vorm === 'rond') {
      return parseFloat(diameterCm) > 0
    }
    if (vorm === 'overig') {
      return parseFloat(oppervlakteM2) > 0
    }
    return false
  }, [vorm, lengteCm, breedteCm, diameterCm, oppervlakteM2, selectedPackageSku])

  async function handleSubmit() {
    if (!isValid || !pricing) return

    setIsSubmitting(true)
    setError(null)

    try {
      const request: Omit<AddVloerkleedRequest, 'token'> = {
        vorm,
        packageSku: selectedPackageSku,
        ...(vorm === 'rechthoekig' && {
          lengteCm: parseFloat(lengteCm),
          breedteCm: parseFloat(breedteCm)
        }),
        ...(vorm === 'rond' && {
          diameterCm: parseFloat(diameterCm)
        }),
        ...(vorm === 'overig' && {
          oppervlakteM2: parseFloat(oppervlakteM2)
        })
      }

      const success = isEdit && editTarget && onEditVloerkleed
        ? await onEditVloerkleed({ ...request, itemId: editTarget.itemId })
        : onAddVloerkleed
          ? await onAddVloerkleed(request)
          : false

      if (success) {
        setSuccess(true)
        setTimeout(() => {
          onClose()
        }, 1500)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg transform transition-all max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-md border-b border-hairline z-10">
            <h2 className="font-heading text-heading-card text-ink">
              {isEdit
                ? `Vloerkleed${editTarget?.volgnummer ? ` ${editTarget.volgnummer}` : ''} wijzigen`
                : 'Vloerkleed toevoegen'}
            </h2>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink transition-colors"
            >
              <Icon name="x" />
            </button>
          </div>

          {/* Content */}
          <div className="p-md">
            {isLoadingPricing ? (
              <div className="flex items-center justify-center py-xl">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !pricing?.kanToevoegen ? (
              <div className="text-center py-lg">
                <Icon name="alert" className="w-12 h-12 text-amber-500 mx-auto mb-md" />
                <p className="text-body text-ink font-medium">
                  {isEdit
                    ? 'Je kunt dit vloerkleed niet meer wijzigen'
                    : 'Je kunt geen vloerkleden meer toevoegen'}
                </p>
                <p className="text-body-sm text-ink-muted mt-xs">
                  De order is al opgehaald of in behandeling.
                </p>
              </div>
            ) : success ? (
              <div className="text-center py-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-md">
                  <Icon name="check" className="w-8 h-8" />
                </div>
                <p className="text-body text-ink font-medium">
                  {isEdit ? 'Vloerkleed gewijzigd!' : 'Vloerkleed toegevoegd!'}
                </p>
              </div>
            ) : (
              <>
                {/* Vorm selectie */}
                <div className="mb-lg">
                  <label className="block text-body font-medium text-ink mb-sm">
                    Vorm
                  </label>
                  <div className="grid grid-cols-3 gap-sm">
                    {(['rechthoekig', 'rond', 'overig'] as VloerkleedVorm[]).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVorm(v)}
                        className={`
                          py-sm px-md rounded-lg border text-body font-medium transition-all
                          ${vorm === v
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border-subtle text-ink-muted hover:border-primary/50'
                          }
                        `}
                      >
                        {v.charAt(0).toUpperCase() + v.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Afmetingen input */}
                <div className="mb-lg">
                  <label className="block text-body font-medium text-ink mb-sm">
                    Afmetingen
                  </label>

                  {vorm === 'rechthoekig' && (
                    <div className="grid grid-cols-2 gap-sm">
                      <div>
                        <label className="block text-body-sm text-ink-muted mb-xs">Lengte (cm)</label>
                        <input
                          type="number"
                          value={lengteCm}
                          onChange={(e) => setLengteCm(e.target.value)}
                          placeholder="bijv. 300"
                          className="input w-full"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-body-sm text-ink-muted mb-xs">Breedte (cm)</label>
                        <input
                          type="number"
                          value={breedteCm}
                          onChange={(e) => setBreedteCm(e.target.value)}
                          placeholder="bijv. 200"
                          className="input w-full"
                          min="1"
                        />
                      </div>
                    </div>
                  )}

                  {vorm === 'rond' && (
                    <div>
                      <label className="block text-body-sm text-ink-muted mb-xs">Diameter (cm)</label>
                      <input
                        type="number"
                        value={diameterCm}
                        onChange={(e) => setDiameterCm(e.target.value)}
                        placeholder="bijv. 250"
                        className="input w-full"
                        min="1"
                      />
                    </div>
                  )}

                  {vorm === 'overig' && (
                    <div>
                      <label className="block text-body-sm text-ink-muted mb-xs">Oppervlakte (m²)</label>
                      <input
                        type="number"
                        value={oppervlakteM2}
                        onChange={(e) => setOppervlakteM2(e.target.value)}
                        placeholder="bijv. 4.5"
                        className="input w-full"
                        min="0.1"
                        step="0.1"
                      />
                    </div>
                  )}

                  {/* Berekende oppervlakte */}
                  {liveCalc && (
                    <p className="text-body-sm text-ink-muted mt-sm">
                      Oppervlakte: {liveCalc.oppervlakte.toString().replace('.', ',')} m²
                    </p>
                  )}
                </div>

                {/* Wasbeurt/Package selectie */}
                <div className="mb-lg">
                  <label className="block text-body font-medium text-ink mb-sm">
                    Wasbeurt
                  </label>
                  <div className="space-y-sm">
                    {pricing?.packages
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((pkg) => {
                        const isSelected = selectedPackageSku === pkg.sku
                        // Bereken indicatieve prijs voor dit pakket
                        const oppervlakte = liveCalc?.oppervlakte || 0
                        const pkgPrijs = oppervlakte > 0
                          ? berekenPrijsExBtw(oppervlakte, pricing.basePricePerM2, pkg.percentage, pricing.regioFactor) * BTW_PERCENTAGE
                          : 0

                        return (
                          <button
                            key={pkg.sku}
                            type="button"
                            onClick={() => setSelectedPackageSku(pkg.sku)}
                            className={`
                              w-full p-md rounded-kleda-md border-2 text-left transition-all relative
                              ${isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-border-subtle hover:border-primary/50'
                              }
                            `}
                          >
                            {pkg.isMeestGekozen && (
                              <span className="absolute -top-2 right-3 bg-secondary text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                Meest gekozen
                              </span>
                            )}
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-sm">
                                <div className={`
                                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                  ${isSelected ? 'border-primary bg-primary' : 'border-border-subtle'}
                                `}>
                                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                                <div>
                                  <p className="text-body font-medium text-ink">
                                    {pkg.label}
                                  </p>
                                  {pkg.description && (
                                    <p className="text-body-sm text-ink-muted mt-xs">
                                      {pkg.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {pkgPrijs > 0 && (
                                <span className="text-body font-semibold text-ink">
                                  € {pkgPrijs.toFixed(2).replace('.', ',')}
                                </span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                  </div>
                </div>

                {/* Bundelkorting info */}
                {liveCalc?.bundelkorting && (
                  <div className="bg-green-50 border border-green-200 rounded-kleda-md p-md mb-lg">
                    <div className="flex items-center gap-sm">
                      <div className="text-green-600">
                        <Icon name="check" />
                      </div>
                      <div>
                        <p className="text-body font-medium text-green-800">
                          {liveCalc.bundelkorting.label}
                        </p>
                        <p className="text-body-sm text-green-600">
                          {Math.round(liveCalc.bundelkorting.discountPercent * 100)}% korting op je hele bestelling
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Prijs samenvatting */}
                {liveCalc && (
                  <div className="bg-surface-soft rounded-kleda-md p-md mb-lg">
                    <div className="space-y-xs text-body">
                      <div className="flex justify-between">
                        <span className="text-ink-muted">Dit vloerkleed</span>
                        <span className="text-ink font-medium">
                          € {liveCalc.prijsInclBtw.toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                      {liveCalc.bundelkorting && liveCalc.kortingBedrag > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Bundelkorting</span>
                          <span className="font-medium">
                            - € {(liveCalc.kortingBedrag * BTW_PERCENTAGE).toFixed(2).replace('.', ',')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body">
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-sm">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="btn-outline flex-1"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!isValid || isSubmitting}
                    className="btn-primary flex-1"
                  >
                    {isEdit
                      ? (isSubmitting ? 'Opslaan...' : 'Opslaan')
                      : (isSubmitting ? 'Toevoegen...' : 'Toevoegen')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// CALENDAR VIEW - Route opties als kalender
// =============================================================================

interface CalendarViewProps {
  opties: RouteOptie[]
  selectedRouteId: string | null
  onSelect: (optie: RouteOptie) => void
  isSubmitting?: boolean
  onLoadMore?: () => Promise<void>
  showLoadMore?: boolean
}

/**
 * Parse route optie label naar datum
 * Label format: "Maandag 15 januari 09:00-12:00"
 */
function parseOptieNaarDatum(optie: RouteOptie): Date | null {
  // Als er een datum veld is, gebruik dat
  if (optie.datum) {
    return new Date(optie.datum)
  }

  // Anders probeer te parsen uit label
  const maanden: Record<string, number> = {
    'januari': 0, 'februari': 1, 'maart': 2, 'april': 3, 'mei': 4, 'juni': 5,
    'juli': 6, 'augustus': 7, 'september': 8, 'oktober': 9, 'november': 10, 'december': 11
  }

  // Match pattern: "Dag DD maand"
  const match = optie.label.match(/(\d{1,2})\s+(januari|februari|maart|april|mei|juni|juli|augustus|september|oktober|november|december)/i)
  if (match) {
    const dag = parseInt(match[1])
    const maand = maanden[match[2].toLowerCase()]
    const jaar = new Date().getFullYear()
    const datum = new Date(jaar, maand, dag)

    // Als datum in het verleden is, neem volgend jaar
    if (datum < new Date()) {
      datum.setFullYear(jaar + 1)
    }
    return datum
  }

  return null
}

const MAAND_NAMEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'
]

const DAG_NAMEN_VOLLEDIG = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag']

interface DagMetOpties {
  datum: Date
  datumKey: string
  opties: RouteOptie[]
}

/**
 * Groepeer opties per dag en sorteer op datum (startend vanaf vandaag)
 */
function groepeerEnSorteerOpties(opties: RouteOptie[]): DagMetOpties[] {
  const perDag = new Map<string, RouteOptie[]>()
  const vandaag = new Date()
  vandaag.setHours(0, 0, 0, 0)

  opties.forEach(optie => {
    const datum = parseOptieNaarDatum(optie)
    if (datum && datum >= vandaag) {
      const key = datum.toISOString().split('T')[0]
      const bestaand = perDag.get(key) || []
      perDag.set(key, [...bestaand, optie])
    }
  })

  // Sorteer per dag
  const gesorteerd: DagMetOpties[] = []
  perDag.forEach((dagOpties, datumKey) => {
    gesorteerd.push({
      datum: new Date(datumKey),
      datumKey,
      opties: dagOpties
    })
  })

  gesorteerd.sort((a, b) => a.datum.getTime() - b.datum.getTime())
  return gesorteerd
}

/**
 * Extract tijdslot uit optie label
 */
function extractTijdslot(optie: RouteOptie): string {
  const tijdMatch = optie.label.match(/\d{1,2}:\d{2}\s*[-–]\s*\d{1,2}:\d{2}/)
  return tijdMatch ? tijdMatch[0] : optie.tijdslot || ''
}

export function CalendarView({
  opties,
  selectedRouteId,
  onSelect,
  isSubmitting = false,
  onLoadMore,
  showLoadMore = false
}: CalendarViewProps) {
  const [isLoading, setIsLoading] = useState(false)

  const dagenMetOpties = useMemo(() => groepeerEnSorteerOpties(opties), [opties])

  const vandaag = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const handleLoadMore = async () => {
    if (!onLoadMore) return
    setIsLoading(true)
    try {
      await onLoadMore()
    } finally {
      setIsLoading(false)
    }
  }

  // Check of datum vandaag is
  const isVandaag = (datum: Date) => datum.getTime() === vandaag.getTime()

  // Check of datum morgen is
  const isMorgen = (datum: Date) => {
    const morgen = new Date(vandaag)
    morgen.setDate(morgen.getDate() + 1)
    return datum.getTime() === morgen.getTime()
  }

  if (dagenMetOpties.length === 0) {
    return (
      <div className="text-center py-lg text-ink-muted">
        <Icon name="calendar" className="w-12 h-12 mx-auto mb-md opacity-30" />
        <p className="text-body">Geen beschikbare momenten gevonden</p>
        {showLoadMore && onLoadMore && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoading || isSubmitting}
            className="mt-md text-primary hover:text-primary-dark text-body-sm font-medium disabled:opacity-50"
          >
            {isLoading ? 'Laden...' : 'Meer momenten laden'}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-sm">
      {dagenMetOpties.map((dag) => {
        const dagNaam = DAG_NAMEN_VOLLEDIG[dag.datum.getDay()]
        const dagNummer = dag.datum.getDate()
        const maandKort = MAAND_NAMEN[dag.datum.getMonth()].slice(0, 3)
        const dagLabel = isVandaag(dag.datum) ? 'Vandaag' : isMorgen(dag.datum) ? 'Morgen' : dagNaam.charAt(0).toUpperCase() + dagNaam.slice(1)

        return (
          <div key={dag.datumKey} className="bg-white border border-border-subtle rounded-kleda-md overflow-hidden">
            {/* Datum header */}
            <div className="px-md py-sm border-b border-hairline">
              <div className="flex items-center gap-sm">
                <div className="text-primary rounded-kleda-sm px-sm py-xs text-center min-w-[3rem]">
                  <span className="text-body font-bold">{dagNummer}</span>
                  <span className="text-body-sm ml-1">{maandKort}</span>
                </div>
                <span className="text-body font-medium text-ink">{dagLabel}</span>
                {isVandaag(dag.datum) && (
                  <span className="ml-auto bg-secondary/20 text-secondary-dark text-body-sm px-sm py-xs rounded-full">
                    Vandaag
                  </span>
                )}
              </div>
            </div>

            {/* Tijdsloten */}
            <div className="p-sm space-y-xs">
              {dag.opties.map((optie) => {
                const isSelected = selectedRouteId === optie.routeId
                const tijdslot = extractTijdslot(optie)

                return (
                  <button
                    key={optie.routeId}
                    type="button"
                    onClick={() => onSelect(optie)}
                    disabled={isSubmitting}
                    className={`
                      w-full text-left px-md py-sm rounded-lg transition-all flex items-center gap-sm
                      ${isSelected
                        ? 'bg-primary text-white'
                        : 'hover:bg-primary/5'
                      }
                      ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {/* Radio indicator */}
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'border-white bg-white' : 'border-border-subtle'}
                    `}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>

                    {/* Tijdslot */}
                    <div className="flex items-center gap-xs flex-1">
                      <Icon name="clock" className={`w-4 h-4 ${isSelected ? 'text-white/80' : 'text-ink-muted'}`} />
                      <span className={`text-body font-medium ${isSelected ? 'text-white' : 'text-ink'}`}>
                        {tijdslot || 'Hele dag'}
                      </span>
                    </div>

                    {/* Capaciteit indicator */}
                    {optie.capaciteit && optie.capaciteit <= 3 && (
                      <span className={`text-body-sm ${isSelected ? 'text-white/80' : 'text-amber-600'}`}>
                        Nog {optie.capaciteit} {optie.capaciteit === 1 ? 'plek' : 'plekken'}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Meer opties laden */}
      {showLoadMore && onLoadMore && (
        <button
          type="button"
          onClick={handleLoadMore}
          disabled={isLoading || isSubmitting}
          className="w-full text-center text-primary hover:text-primary-dark text-body font-medium py-md mt-sm disabled:opacity-50 border border-dashed border-primary/30 rounded-full hover:border-primary hover:bg-primary/5 transition-all"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-sm">
              <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Laden...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-sm">
              <Icon name="plus" className="w-5 h-5" />
              Meer momenten tonen
            </span>
          )}
        </button>
      )}
    </div>
  )
}

// =============================================================================
// COUNTDOWN TIMER - Tijd tot afspraak
// =============================================================================

interface CountdownTimerProps {
  targetDate: Date | string
  label?: string
  className?: string
}

interface TimeRemaining {
  dagen: number
  uren: number
  minuten: number
  seconden: number
  totaalMs: number
}

function berekenTijdOver(target: Date): TimeRemaining {
  const nu = new Date().getTime()
  const verschil = target.getTime() - nu

  if (verschil <= 0) {
    return { dagen: 0, uren: 0, minuten: 0, seconden: 0, totaalMs: 0 }
  }

  const dagen = Math.floor(verschil / (1000 * 60 * 60 * 24))
  const uren = Math.floor((verschil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minuten = Math.floor((verschil % (1000 * 60 * 60)) / (1000 * 60))
  const seconden = Math.floor((verschil % (1000 * 60)) / 1000)

  return { dagen, uren, minuten, seconden, totaalMs: verschil }
}

// =============================================================================
// AFSPRAAK STATUS - rustige stand van zaken binnen 24 uur (geen teller)
// =============================================================================

interface AfspraakStatusProps {
  /** Start van het tijdsblok (ISO). Hoort bij hetzelfde label als `tot`. */
  vanaf: string
  /** Einde van het tijdsblok (ISO). */
  tot?: string
  className?: string
}

type AfspraakFase = 'morgen' | 'vandaag' | 'bezig' | 'verstreken'

function bepaalFase(vanaf: Date, tot: Date | null, nu: Date): AfspraakFase {
  if (tot && nu > tot) return 'verstreken'
  if (nu >= vanaf) return 'bezig'

  const isVandaag = vanaf.getFullYear() === nu.getFullYear()
    && vanaf.getMonth() === nu.getMonth()
    && vanaf.getDate() === nu.getDate()

  return isVandaag ? 'vandaag' : 'morgen'
}

const AFSPRAAK_FASE_TEKST: Record<AfspraakFase, string> = {
  morgen: 'Morgen',
  vandaag: 'Vandaag',
  bezig: 'Je tijdsblok loopt nu',
  verstreken: 'Iets later dan gepland',
}

/**
 * Toont binnen 24 uur alleen de stand van zaken, geen aftelklok.
 *
 * Bewust geen teller: het tijdsblok is twee uur breed, dus een klok die
 * seconden wegtikt belooft een aankomsttijd die we niet hebben. Klanten lazen
 * dat als "ze zijn er over 20 minuten" en stonden ruim te vroeg klaar.
 */
export function AfspraakStatus({ vanaf, tot, className = '' }: AfspraakStatusProps) {
  const start = useMemo(() => new Date(vanaf), [vanaf])
  const eind = useMemo(() => (tot ? new Date(tot) : null), [tot])

  // Eén keer per minuut herijken, zodat de stand niet blijft hangen op een
  // pagina die lang open staat. Niet zichtbaar als teller.
  const [nu, setNu] = useState(() => new Date())
  useEffect(() => {
    const interval = setInterval(() => setNu(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const fase = bepaalFase(start, eind, nu)
  const isAandacht = fase === 'verstreken'

  return (
    <div className={className}>
      <div className={`
        inline-flex items-center gap-sm px-md py-sm rounded-full
        ${isAandacht ? 'bg-amber-50 text-amber-700' : 'bg-secondary/10 text-secondary'}
      `}>
        <Icon name={isAandacht ? 'clock' : 'check'} className="w-5 h-5" />
        <span className="text-body font-medium">{AFSPRAAK_FASE_TEKST[fase]}</span>
      </div>

      {fase === 'verstreken' && (
        <p className="mt-sm text-body-sm text-ink-muted">
          Je staat nog op de route van vandaag.
        </p>
      )}
    </div>
  )
}

export function CountdownTimer({ targetDate, label, className = '' }: CountdownTimerProps) {
  const target = useMemo(() =>
    typeof targetDate === 'string' ? new Date(targetDate) : targetDate,
    [targetDate]
  )

  const [tijdOver, setTijdOver] = useState<TimeRemaining>(() => berekenTijdOver(target))

  useEffect(() => {
    const interval = setInterval(() => {
      setTijdOver(berekenTijdOver(target))
    }, 1000)

    return () => clearInterval(interval)
  }, [target])

  if (tijdOver.totaalMs <= 0) {
    return (
      <div className={`text-center ${className}`}>
        <div className="inline-flex items-center gap-sm bg-green-50 text-green-700 px-md py-sm rounded-full">
          <Icon name="check" className="w-5 h-5" />
          <span className="text-body font-medium">Vandaag!</span>
        </div>
      </div>
    )
  }

  // Bepaal of het binnenkort is (< 24 uur)
  const isBinnenkort = tijdOver.dagen === 0 && tijdOver.uren < 24

  // Bereken weken voor langere periodes
  const weken = Math.floor(tijdOver.dagen / 7)
  const extraDagen = tijdOver.dagen % 7
  const isLangWeg = tijdOver.dagen > 14

  return (
    <div className={className}>
      {label && (
        <p className="text-body-sm text-ink-muted mb-sm">{label}</p>
      )}

      {/* Vriendelijke tekst voor lange periodes */}
      {isLangWeg && (
        <p className="text-body-sm text-ink-muted mb-xs">
          Over {weken} {weken === 1 ? 'week' : 'weken'}{extraDagen > 0 ? ` en ${extraDagen} ${extraDagen === 1 ? 'dag' : 'dagen'}` : ''}
        </p>
      )}

      <div className={`
        inline-flex items-center gap-xs sm:gap-sm p-sm sm:p-md rounded-kleda-lg
        ${isBinnenkort ? 'bg-amber-50' : 'bg-primary/5'}
      `}>
        {tijdOver.dagen > 0 && (
          <>
            <div className="flex flex-col items-center min-w-[3rem]">
              <span className={`text-heading-main font-bold ${isBinnenkort ? 'text-amber-600' : 'text-primary'}`}>
                {tijdOver.dagen}
              </span>
              <span className="text-body-sm text-ink-muted">
                {tijdOver.dagen === 1 ? 'dag' : 'dagen'}
              </span>
            </div>
            <span className={`text-heading-main font-light ${isBinnenkort ? 'text-amber-400' : 'text-primary/30'}`}>:</span>
          </>
        )}

        <div className="flex flex-col items-center min-w-[3rem]">
          <span className={`text-heading-main font-bold ${isBinnenkort ? 'text-amber-600' : 'text-primary'}`}>
            {String(tijdOver.uren).padStart(2, '0')}
          </span>
          <span className="text-body-sm text-ink-muted">uur</span>
        </div>

        <span className={`text-heading-main font-light ${isBinnenkort ? 'text-amber-400' : 'text-primary/30'}`}>:</span>

        <div className="flex flex-col items-center min-w-[3rem]">
          <span className={`text-heading-main font-bold ${isBinnenkort ? 'text-amber-600' : 'text-primary'}`}>
            {String(tijdOver.minuten).padStart(2, '0')}
          </span>
          <span className="text-body-sm text-ink-muted">min</span>
        </div>

        {tijdOver.dagen === 0 && (
          <>
            <span className={`text-heading-main font-light ${isBinnenkort ? 'text-amber-400' : 'text-primary/30'}`}>:</span>
            <div className="flex flex-col items-center min-w-[3rem]">
              <span className={`text-heading-main font-bold tabular-nums ${isBinnenkort ? 'text-amber-600' : 'text-primary'}`}>
                {String(tijdOver.seconden).padStart(2, '0')}
              </span>
              <span className="text-body-sm text-ink-muted">sec</span>
            </div>
          </>
        )}
      </div>

      {isBinnenkort && (
        <p className="text-body-sm text-amber-600 font-medium mt-sm">
          Bijna zover! 🎉
        </p>
      )}
    </div>
  )
}

// =============================================================================
// PULL TO REFRESH - Swipe down om te refreshen
// =============================================================================

interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => Promise<void>
  isRefreshing?: boolean
  threshold?: number
  className?: string
}

export function PullToRefresh({
  children,
  onRefresh,
  isRefreshing = false,
  threshold = 80,
  className = ''
}: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pullDistance, setPullDistance] = useState(0)
  const [isPulling, setIsPulling] = useState(false)
  const startY = useRef<number>(0)
  const currentY = useRef<number>(0)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Alleen activeren als we bovenaan de pagina zijn
    if (window.scrollY > 0) return

    startY.current = e.touches[0].clientY
    setIsPulling(true)
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling || isRefreshing) return

    currentY.current = e.touches[0].clientY
    const distance = Math.max(0, currentY.current - startY.current)

    // Resistance effect - hoe verder je trekt, hoe moeilijker
    const resistedDistance = Math.min(distance * 0.5, threshold * 1.5)
    setPullDistance(resistedDistance)

    // Voorkom default scroll gedrag als we pullen
    if (distance > 10) {
      e.preventDefault()
    }
  }, [isPulling, isRefreshing, threshold])

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return

    if (pullDistance >= threshold && !isRefreshing) {
      // Trigger refresh
      await onRefresh()
    }

    setIsPulling(false)
    setPullDistance(0)
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  const progress = Math.min(pullDistance / threshold, 1)
  const shouldTrigger = pullDistance >= threshold

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Pull indicator */}
      <div
        className={`
          absolute left-1/2 -translate-x-1/2 flex items-center justify-center
          transition-opacity duration-200
          ${pullDistance > 10 || isRefreshing ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          top: Math.max(8, pullDistance - 40),
          transform: `translateX(-50%) rotate(${progress * 360}deg)`
        }}
      >
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center
          transition-all duration-200
          ${shouldTrigger || isRefreshing
            ? 'bg-primary text-white scale-110'
            : 'bg-surface-soft text-primary'
          }
        `}>
          {isRefreshing ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Icon
              name="refresh"
              className={`w-5 h-5 transition-transform ${shouldTrigger ? 'rotate-180' : ''}`}
            />
          )}
        </div>
      </div>

      {/* Pull hint text */}
      {(pullDistance > 20 || isRefreshing) && (
        <div
          className="absolute left-1/2 -translate-x-1/2 text-body-sm text-ink-muted text-center"
          style={{ top: Math.max(48, pullDistance) }}
        >
          {isRefreshing
            ? 'Laden...'
            : shouldTrigger
              ? 'Loslaten om te verversen'
              : 'Trek omlaag om te verversen'
          }
        </div>
      )}

      {/* Content with pull offset */}
      <div
        style={{
          transform: `translateY(${isPulling || isRefreshing ? pullDistance : 0}px)`,
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  )
}

// =============================================================================
// VIEW TOGGLE - Lijst/Kalender weergave
// =============================================================================

export type ViewMode = 'list' | 'calendar'

interface ViewToggleProps {
  mode: ViewMode
  onChange: (mode: ViewMode) => void
  className?: string
}

export function ViewToggle({ mode, onChange, className = '' }: ViewToggleProps) {
  return (
    <div className={`inline-flex bg-surface-soft rounded-kleda-md p-1 ${className}`}>
      <button
        type="button"
        onClick={() => onChange('list')}
        className={`
          px-sm py-xs rounded-kleda-sm text-body-sm font-medium transition-all
          ${mode === 'list'
            ? 'bg-white text-primary shadow-sm'
            : 'text-ink-muted hover:text-ink'
          }
        `}
      >
        Lijst
      </button>
      <button
        type="button"
        onClick={() => onChange('calendar')}
        className={`
          px-sm py-xs rounded-kleda-sm text-body-sm font-medium transition-all
          ${mode === 'calendar'
            ? 'bg-white text-primary shadow-sm'
            : 'text-ink-muted hover:text-ink'
          }
        `}
      >
        Kalender
      </button>
    </div>
  )
}

// =============================================================================
// OPSLAGKOSTEN BADGE - Toont opslagkosten voor aflevermomenten
// =============================================================================

import { calculateOpslagkosten, type OpslagkostenResult } from '../lib/opslagkosten'

interface OpslagkostenBadgeProps {
  /** Startdatum voor berekening (ISO string) - wanneer aflevervoorstel is verstuurd */
  startDatum?: string
  /** Aflevermoment datum (ISO string) */
  afleverDatum?: string
  /** Optionele extra CSS classes */
  className?: string
  /** Toon compacte variant zonder weken detail */
  compact?: boolean
}

/**
 * OpslagkostenBadge - Premium badge component voor opslagkosten weergave
 *
 * Toont een visueel onderscheid tussen gratis en betaalde opslagkosten:
 * - Gratis: Subtiele groene badge
 * - Kosten: Amber/oranje badge met bedrag
 *
 * @example
 * ```tsx
 * <OpslagkostenBadge
 *   startDatum="2024-01-01T10:00:00Z"
 *   afleverDatum="2024-01-30"
 * />
 * ```
 */
export function OpslagkostenBadge({
  startDatum,
  afleverDatum,
  className = '',
  compact = false,
}: OpslagkostenBadgeProps) {
  // Geen badge tonen als we geen startdatum hebben
  if (!startDatum) return null

  const result: OpslagkostenResult = calculateOpslagkosten(startDatum, afleverDatum)

  if (result.isGratis) {
    // Gratis - subtiele groene indicator
    return (
      <span
        className={`
          inline-flex items-center gap-1
          text-body-sm font-medium
          text-green-700
          ${className}
        `}
        title="Aflevering binnen gratis periode van 28 dagen"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {!compact && <span>Gratis</span>}
      </span>
    )
  }

  // Kosten - amber badge
  const weekLabel = result.weken === 1 ? 'week' : 'weken'

  return (
    <span
      className={`
        inline-flex items-center gap-1
        text-body-sm font-medium
        text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full
        ${className}
      `}
      title={`${result.weken} ${weekLabel} opslagkosten á €10/week`}
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {compact ? (
        <span>+€{result.bedrag}</span>
      ) : (
        <span>+€{result.bedrag} opslag</span>
      )}
    </span>
  )
}
